import React, { useEffect, useMemo, useState } from 'react';
import { AutocompleteChangeValue, IAutocompleteOption } from 'edenred-ui';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';

import { goToPage } from '@epi-actions/navigation';
import { getApplicationLanguageId } from '@epi-store/application/application.selectors';
import { useGetCompanyBeneficiariesQuery } from '@epi-api/companyBeneficiaries';
import { routerPaths } from '@epi-constants/routerPaths';
import { useOnboardingWizard } from '@epi-shared/hooks';
import {
  BeneficiaryItemsPerPage,
  SortByLastName
} from '@epi-constants/beneficiary';
import { sliceRows, sortRows } from '@epi-helpers/SortHelpers';
import { useGetBenefitGroupsQuery } from '@epi-api/benefitGroups';
import {
  isAutocompleteOption,
  isAutocompleteOptionArray,
  getUniqueValuesArray
} from '@epi-helpers/AutocompleteHelper';

export const useBeneficiariesList = () => {
  const location = useLocation();
  const history = useHistory();
  const params = new URLSearchParams(location.search);
  const preselectedGroupId = params.get('groupId');
  const language = useSelector(getApplicationLanguageId);
  const { t } = useTranslation();
  const { data: groupSettings, isLoading: isGroupSettingsLoading } =
    useGetBenefitGroupsQuery();
  const [showNotification, setShowNotification] = useState(false);
  const [preselectedGroupName, setPreselectedGroupName] = useState('');
  const [showBeneficiaryModal, setshowBeneficiaryModal] = useState(false);
  const [selectedBeneficiaryId, setSelectedBeneficiaryId] = useState('');
  const [isEmployeeAdded, setIsEmployeeAdded] = useState(false);
  const [page, setPage] = useState(1);
  const [selectedEmployees, setSelectedEmployees] = useState<
    IAutocompleteOption[]
  >([]);
  const [selectedBenefitGroup, setSelectedBenefitGroup] =
    useState<IAutocompleteOption>();
  const [selectedEmploymentType, setSelectedEmploymentType] =
    useState<IAutocompleteOption>();
  const { isLoading: isGetEmployeesLoading, data: employees } =
    useGetCompanyBeneficiariesQuery();
  const reduxDispatch = useDispatch();
  const {
    isOnBoardingActive,
    flags,
    isLoading: isOnboardingLoading,
    handleGoOnboardingPayment
  } = useOnboardingWizard();

  useEffect(() => {
    setPage(1);
  }, [selectedEmployees]);

  const benefitGroupOptions = useMemo(
    () =>
      groupSettings?.map(group => ({
        label: group.groupName,
        value: group.groupId,
        subLabel: employees?.beneficiaries
          .filter(employee => employee.groupId === group.groupId)
          .map(employee => `${employee.lastName} ${employee.firstName}`)
          .join(', ')
      })) || [],
    [groupSettings]
  );

  useEffect(() => {
    if (preselectedGroupId && benefitGroupOptions.length > 0) {
      const matchingOption = benefitGroupOptions.find(
        option => option.value === Number(preselectedGroupId)
      );
      if (matchingOption) {
        setSelectedBenefitGroup(matchingOption);
      }
    }
  }, [preselectedGroupId, benefitGroupOptions]);

  const handleClearPreselectedGroup = () => {
    setSelectedBenefitGroup(undefined);
    params.delete('groupId');
    const newSearch = params.toString();
    const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}${
      location.hash
    }`;
    history.replace(newUrl);
  };

  const filteredEmployees = useMemo(() => {
    if (!employees?.beneficiaries.length) {
      return [];
    }
    let filtered = employees.beneficiaries;

    if (selectedEmployees.length > 0) {
      const selectedPersonIds = selectedEmployees.map(employee =>
        String(employee.value)
      );
      filtered = filtered.filter(employee =>
        selectedPersonIds.includes(String(employee.personId))
      );
    }

    if (selectedBenefitGroup) {
      const selectedGroupId = Number(selectedBenefitGroup.value);
      const filteredTemp = filtered.filter(
        employee => employee.groupId === selectedGroupId
      );
      if (filteredTemp.length === 0 && preselectedGroupId) {
        setShowNotification(true);
        setPreselectedGroupName(selectedBenefitGroup.label);
        handleClearPreselectedGroup();
      } else {
        filtered = filteredTemp;
      }
    }

    if (selectedEmploymentType) {
      const selectedType = String(selectedEmploymentType.value);
      filtered = filtered.filter(
        employee => employee.employmentType === selectedType
      );
    }
    return filtered;
  }, [
    employees?.beneficiaries,
    selectedEmployees,
    selectedBenefitGroup,
    selectedEmploymentType,
    preselectedGroupId
  ]);

  const sortedEmployees = useMemo(() => {
    if (filteredEmployees.length) {
      const sorted = sortRows(filteredEmployees, false, SortByLastName);
      return sliceRows(sorted, page, BeneficiaryItemsPerPage);
    }
    return [];
  }, [filteredEmployees, page]);

  const paginationCount = Math.ceil(
    filteredEmployees.length / BeneficiaryItemsPerPage
  );

  const employeesOptions = useMemo(() => {
    return (
      employees?.beneficiaries
        ?.map(employee => ({
          label: `${employee.lastName} ${employee.firstName}`,
          value: employee.personId
        }))
        .sort((a, b) =>
          a.label.toLowerCase().localeCompare(b.label.toLowerCase())
        ) || []
    );
  }, [employees?.beneficiaries]);

  const employmentTypeOptions = useMemo(
    () =>
      getUniqueValuesArray(employees?.beneficiaries, b => b.employmentType).map(
        value => ({
          label: t(`containers.beneficiaries.detail.${value}`),
          value
        })
      ),
    [employees?.beneficiaries, language]
  );

  const showBeneficiaryAddedNotification =
    isEmployeeAdded &&
    isOnBoardingActive &&
    employees?.beneficiaries.some(b => b.isNew === true);

  const showDetail = (beneficiaryId: string) => {
    setSelectedBeneficiaryId(beneficiaryId);
    setshowBeneficiaryModal(true);
  };

  const handleClose = () => {
    setshowBeneficiaryModal(false);
    setSelectedBeneficiaryId('');
  };

  const handleCloseNotification = () => {
    setShowNotification(false);
  };

  const footer = isOnBoardingActive
    ? {
        backText: t('components.onboarding.buttons.back'),
        submitText: t('components.onboarding.buttons.next'),
        onBack: () => {
          reduxDispatch(goToPage(routerPaths.benefitGroups));
        },
        onSubmit: handleGoOnboardingPayment,
        disableSubmit: !flags?.isEmployeesSet,
        submitButtonWidth: '240px'
      }
    : undefined;

  const handleEmployeesChange = (
    event: React.SyntheticEvent<Element, Event>,
    newValue: AutocompleteChangeValue
  ) => {
    if (isAutocompleteOptionArray(newValue)) {
      setSelectedEmployees(newValue);
      setPage(1);
    } else {
      setSelectedEmployees([]);
    }
  };

  const handleGroupChange = (
    event: React.SyntheticEvent<Element, Event>,
    newValue: AutocompleteChangeValue
  ) => {
    if (isAutocompleteOption(newValue)) {
      setSelectedBenefitGroup(newValue);
      setPage(1);
    } else {
      setSelectedBenefitGroup(undefined);
      params.delete('groupId');
      const newSearch = params.toString();
      const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}${
        location.hash
      }`;
      history.replace(newUrl);
      handleCloseNotification();
    }
  };

  const handleEmploymentTypeChange = (
    event: React.SyntheticEvent<Element, Event>,
    newValue: AutocompleteChangeValue
  ) => {
    if (isAutocompleteOption(newValue)) {
      setSelectedEmploymentType(newValue);
      setPage(1);
    } else {
      setSelectedEmploymentType(undefined);
    }
  };

  return {
    isGetEmployeesLoading,
    isGroupSettingsLoading,
    isOnboardingLoading,
    groupSettings,
    employees,
    filteredEmployees,
    selectedBeneficiaryId,
    showBeneficiaryAddedNotification,
    handleEmployeesChange,
    handleGroupChange,
    handleEmploymentTypeChange,
    showBeneficiaryModal,
    handleClose,
    setIsEmployeeAdded,
    setPage,
    page,
    paginationCount,
    sortedEmployees,
    showDetail,
    employeesOptions,
    benefitGroupOptions,
    employmentTypeOptions,
    selectedBenefitGroup,
    selectedEmploymentType,
    setshowBeneficiaryModal,
    preselectedGroupId,
    showNotification,
    handleCloseNotification,
    preselectedGroupName,
    footer
  };
};
